import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import TripProgressTracker from "@/components/rider/TripProgressTracker";
import { useToast } from "@/hooks/use-toast";
import { createApiRequest } from "@/utils/apiUtils";
import { DashboardTrip } from "@/types/dashboard";
import { format } from "date-fns";

const DriverTripSync = () => {
  const [searchParams] = useSearchParams();
  const [tripId, setTripId] = useState("");
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4>(1);
  const [tripData, setTripData] = useState<DashboardTrip | null>(null);
  const [isLoadingTrip, setIsLoadingTrip] = useState(false);
  const [isConnectingDriver, setIsConnectingDriver] = useState(false);
  const [isConfirmingDriver, setIsConfirmingDriver] = useState(false);
  const [tripError, setTripError] = useState<string | null>(null);
  const [driverFormData, setDriverFormData] = useState({
    driverName: "",
    driverContact: "",
    vehicleId: ""
  });
  const [routeConfirmation, setRouteConfirmation] = useState("");
  const { toast } = useToast();

  // Auto-populate trip ID and start management if coming from operator list
  useEffect(() => {
    const urlTripId = searchParams.get('tripId');
    const autoStart = searchParams.get('autoStart');
    
    if (urlTripId && autoStart === 'true') {
      setTripId(urlTripId);
      // Automatically fetch trip details
      fetchTripDetails(urlTripId);
    }
  }, [searchParams]);

  const fetchTripDetails = async (id: string) => {
    setIsLoadingTrip(true);
    setTripError(null);
    
    try {
      console.log(`Fetching trip details for ID: ${id}`);
      const request = createApiRequest(`trips/${id}/`);
      const response = await fetch(request);
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Trip not found. Please check the Trip ID and try again.");
        }
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to fetch trip details");
      }
      
      const data = await response.json();
      console.log("✅ Trip details fetched:", data);
      setTripData(data);

      // Determine the correct step based on trip_state
      const tripState = data.trip_state;
      console.log(`Trip state: ${tripState}`);
      
      switch (tripState) {
        case 'CLAIMED':
          console.log("Trip is claimed - directing to Connect Driver form");
          setCurrentStep(2);
          break;
        case 'DRIVER_ASSIGNED':
          console.log("Driver assigned - directing to Confirm Driver Status");
          setCurrentStep(3);
          break;
        case 'DRIVER_CONFIRMED':
          console.log("Driver confirmed - directing to Trip In-Progress");
          setCurrentStep(4);
          break;
        case 'COMPLETED':
          console.log("Trip completed - directing to Trip In-Progress");
          setCurrentStep(4);
          break;
        default:
          console.log(`Unknown trip state: ${tripState} - defaulting to Connect Driver form`);
          setCurrentStep(2);
          break;
      }
    } catch (error) {
      console.error("Error fetching trip details:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to fetch trip details";
      setTripError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoadingTrip(false);
    }
  };

  const handleTripIdSubmit = () => {
    if (!tripId.trim()) {
      toast({
        title: "Missing Trip ID",
        description: "Please enter a Trip ID Number",
        variant: "destructive",
      });
      return;
    }
    fetchTripDetails(tripId.trim());
  };

  const handleDriverFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!driverFormData.driverName || !driverFormData.driverContact || !driverFormData.vehicleId) {
      toast({
        title: "Missing Information",
        description: "Please fill in all driver details",
        variant: "destructive",
      });
      return;
    }

    if (!tripData?.id) {
      toast({
        title: "Error",
        description: "No trip data available",
        variant: "destructive",
      });
      return;
    }

    setIsConnectingDriver(true);

    try {
      console.log(`Connecting driver to trip ${tripData.id}`);
      const request = createApiRequest(
        `trips/${tripData.id}/connect-driver/`, 
        'POST',
        {
          name: driverFormData.driverName,
          contact_info: driverFormData.driverContact,
          vehicle_id: driverFormData.vehicleId
        }
      );

      const response = await fetch(request);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to connect driver to trip");
      }
      
      const data = await response.json();
      console.log("✅ Driver connected successfully:", data);

      toast({
        title: "Driver Connected Successfully",
        description: "The driver has been assigned to the trip",
      });

      // Move to driver on-route state
      setCurrentStep(3);
    } catch (error) {
      console.error("Error connecting driver:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to connect driver";
      toast({
        title: "Connection Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsConnectingDriver(false);
    }
  };

  const handleRouteConfirmation = async () => {
    if (routeConfirmation !== "yes") {
      toast({
        title: "Please Confirm",
        description: "Please confirm that the driver is on route",
        variant: "destructive",
      });
      return;
    }

    if (!tripData?.id) {
      toast({
        title: "Error",
        description: "No trip data available",
        variant: "destructive",
      });
      return;
    }

    setIsConfirmingDriver(true);

    try {
      console.log(`Confirming driver for trip ${tripData.id}`);
      const request = createApiRequest(
        `trips/${tripData.id}/confirm-driver/`, 
        'POST'
      );

      const response = await fetch(request);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to confirm driver status");
      }
      
      const data = await response.json();
      console.log("✅ Driver confirmed successfully:", data);

      toast({
        title: "Driver Status Updated",
        description: "Driver confirmed on route",
      });

      // Move to trip in-progress
      setCurrentStep(4);
    } catch (error) {
      console.error("Error confirming driver:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to confirm driver";
      toast({
        title: "Confirmation Failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsConfirmingDriver(false);
    }
  };

  const renderCurrentForm = () => {
    if (currentStep === 2) {
      return (
        <Card className="bg-white border border-gray-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">Connect Driver to Trip</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDriverFormSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="driverName" className="text-sm font-medium text-gray-900">
                  Driver Name *
                </Label>
                <Input
                  id="driverName"
                  type="text"
                  placeholder="Enter Driver Name"
                  value={driverFormData.driverName}
                  onChange={(e) => setDriverFormData(prev => ({ ...prev, driverName: e.target.value }))}
                  className="bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="driverContact" className="text-sm font-medium text-gray-900">
                  Enter Support Phone Number
                </Label>
                <Input
                  id="driverContact"
                  type="text"
                  placeholder="Enter Driver Contact Information"
                  value={driverFormData.driverContact}
                  onChange={(e) => setDriverFormData(prev => ({ ...prev, driverContact: e.target.value }))}
                  className="bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicleId" className="text-sm font-medium text-gray-900">
                  Vehicle ID # *
                </Label>
                <Input
                  id="vehicleId"
                  type="text"
                  placeholder="Enter Vehicle ID #"
                  value={driverFormData.vehicleId}
                  onChange={(e) => setDriverFormData(prev => ({ ...prev, vehicleId: e.target.value }))}
                  className="bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500"
                />
              </div>

              <Button
                type="submit"
                disabled={isConnectingDriver}
                className="w-full gradient-bg text-black font-semibold py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isConnectingDriver ? "Connecting Driver..." : "Confirm Driver Assignment"}
              </Button>
            </form>
          </CardContent>
        </Card>
      );
    }

    if (currentStep === 3) {
      return (
        <Card className="bg-white border border-gray-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">Confirm Driver Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="space-y-4">
                <Label className="text-sm font-medium text-gray-900">
                  Confirm that the driver is on route
                </Label>
                <RadioGroup 
                  value={routeConfirmation} 
                  onValueChange={setRouteConfirmation}
                  className="flex space-x-6"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="yes" />
                    <Label htmlFor="yes" className="text-gray-900">Yes, driver is on route</Label>
                  </div>
                </RadioGroup>
              </div>

              <Button
                onClick={handleRouteConfirmation}
                disabled={isConfirmingDriver}
                className="w-full gradient-bg text-black font-semibold py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {isConfirmingDriver ? "Confirming..." : "Confirm Driver Status"}
              </Button>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (currentStep === 4) {
      return (
        <Card className="bg-white border border-gray-200 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-gray-900">Trip In Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8">
              <p className="text-gray-700 mb-4">
                Trip is currently in progress. The trip will be automatically marked as complete when the scheduled time ends.
              </p>
              <div className="text-sm text-gray-500">
                Monitor trip progress through the main dashboard.
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }

    return null;
  };

  // Format trip data for display
  const tripDetails = tripData ? {
    id: tripData.id,
    event: tripData.community_event?.name || "Private Trip",
    pickup_time: format(new Date(tripData.pickup_at), "h:mm a"),
    pickup_date: format(new Date(tripData.pickup_at), "MMMM d, yyyy"),
    pickup_location: tripData.from_address?.formatted || "Location not available",
    dropoff_location: tripData.to_address?.formatted || "Location not available",
    passenger_count: tripData.passenger_count || 0,
    duration: tripData.booking_duration || "Not specified",
    vehicle_type: "Party Bus", // This would need to come from vehicle data if available
    price: (tripData as any).rider_price ? `$${(tripData as any).rider_price}` : 
           tripData.organization_price ? `$${tripData.organization_price}` : "N/A",
    notes: tripData.extra?.notes || "No notes provided",
    status: (() => {
      const state = (tripData as any).trip_state;
      switch (state) {
        case 'CLAIMED': return "Trip Booked";
        case 'DRIVER_ASSIGNED': return "Driver Confirmed";
        case 'DRIVER_CONFIRMED': return "Driver On-Route";
        case 'COMPLETED': return "Trip In-Progress";
        default: return "Pending Assignment";
      }
    })()
  } : null;

  return (
    <DashboardLayout>
      <div className="container mx-auto px-6 max-w-7xl bg-gray-100 min-h-screen py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Trip State Management</h1>
          <p className="text-gray-700">Manage trip progression through each state of the journey.</p>
        </div>

        <div className="flex gap-6 mb-8">
          {/* Left Column - Trip Entry (25%) */}
          <div className="w-1/4">
            <Card className="bg-white border border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Enter Trip ID</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="tripId" className="text-sm font-medium text-gray-900">
                      Trip ID Number *
                    </Label>
                    <Input
                      id="tripId"
                      type="text"
                      placeholder="Enter Trip ID Number"
                      value={tripId}
                      onChange={(e) => setTripId(e.target.value)}
                      className="bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500"
                    />
                  </div>
                  <Button
                    onClick={handleTripIdSubmit}
                    disabled={isLoadingTrip}
                    className="w-full gradient-bg text-black font-semibold py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {isLoadingTrip ? "Loading..." : "Begin Trip Management"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Trip Details (75%) */}
          <div className="w-3/4">
            {tripDetails ? (
              <Card className="bg-white border border-gray-200 shadow-lg h-full">
                <CardHeader>
                  <CardTitle className="text-xl text-gray-900">Trip Details</CardTitle>
                  <p className="text-sm text-gray-600">Trip ID: {tripDetails.id}</p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Event</h3>
                        <p className="text-gray-700">{tripDetails.event}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Pickup Time</h3>
                        <p className="text-gray-700">{tripDetails.pickup_time} on {tripDetails.pickup_date}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Pickup Location</h3>
                        <p className="text-gray-700">{tripDetails.pickup_location}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Dropoff Location</h3>
                        <p className="text-gray-700">{tripDetails.dropoff_location}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Passengers</h3>
                        <p className="text-gray-700">{tripDetails.passenger_count} guests</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Duration</h3>
                        <p className="text-gray-700">{tripDetails.duration}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Vehicle Type</h3>
                        <p className="text-gray-700">{tripDetails.vehicle_type}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-1">Price</h3>
                        <p className="text-gray-700 font-semibold text-lg">{tripDetails.price}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Notes</h3>
                      <p className="text-gray-700 max-h-24 overflow-y-auto">{tripDetails.notes}</p>
                    </div>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">Status</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        tripDetails.status === 'In Progress' ? 'bg-green-100 text-green-800' :
                        tripDetails.status === 'Driver Assigned' ? 'bg-blue-100 text-blue-800' :
                        tripDetails.status === 'Finding Driver' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {tripDetails.status}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border border-gray-200 shadow-lg h-full flex flex-col">
                <CardContent className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <p className="text-lg mb-2">No Trip Selected</p>
                    <p className="text-sm">Enter a Trip ID to view trip details</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        {/* Full Width Section - Progress Tracker */}
        <div className="mb-8">
          <Card className="bg-white border border-gray-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Trip Progress</CardTitle>
              {tripId && <p className="text-sm text-gray-600">Trip ID: {tripId}</p>}
            </CardHeader>
            <CardContent>
              <TripProgressTracker currentStep={tripId ? (currentStep > 1 ? currentStep : 0) : 0} />
            </CardContent>
          </Card>
        </div>

        {/* Full Width Section - Dynamic Forms */}
        {renderCurrentForm()}

        {/* Full Width Section - Driver Information (if assigned) */}
        {currentStep >= 2 && driverFormData.driverName && (
          <div className="mt-8">
            <Card className="bg-white border border-gray-200 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900">Driver Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Driver Name</h3>
                    <p className="text-gray-700">{driverFormData.driverName}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Contact</h3>
                    <p className="text-gray-700">{driverFormData.driverContact}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Vehicle ID</h3>
                    <p className="text-gray-700">{driverFormData.vehicleId}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverTripSync;